import { useLayoutEffect, useMemo } from 'react';
import { getWidgetStores } from '@/entities/widget';
import { getWidgetComponentSync } from '@/lib/config/agents.config';
import { resolveArtifactTool } from '@/shared/constants/widget-tool-mapping';

interface Props {
  artifactKey: string | null;
  data: Record<string, unknown> | null | undefined;
  fallback: React.ReactNode;
  /** Extra props forwarded to the mounted widget (e.g. a preview-only view hint). */
  widgetProps?: Record<string, unknown>;
}

/**
 * Resolves the registry-bound widget for `artifactKey` and renders it with
 * `data` — matches `SidePanel`'s mounting contract.
 *
 * Chat-side widgets read their payload from the global `widgetStore` via
 * `getWidgetStores().widgetStore.useWidget()` (see e.g. StrategicTerritoriesWidget).
 * To host them in the modal we seed the SAME store with a `WidgetWithMeta`
 * envelope on mount, then restore the prior widget on unmount so re-entry
 * into the chat side panel preserves whatever it had loaded.
 *
 * Race guard (F1): rapid prev/next clicks can interleave mount/cleanup of
 * adjacent artifacts. The cleanup compares the live store reference against
 * the envelope we seeded; if they differ, another mount has taken over and
 * we leave its seed alone instead of clobbering it with a stale `previous`.
 */
export function ArtifactPreviewWidget(props: Props) {
  // Resolve the tool entry once. When the key isn't registered, fall through
  // to the fallback without ever calling `getWidgetComponentSync` (F15).
  const tool = useMemo(
    () => (props.artifactKey ? resolveArtifactTool(props.artifactKey) : null),
    [props.artifactKey],
  );

  const WidgetComponent = useMemo(() => {
    if (!props.artifactKey || !tool) return undefined;
    return getWidgetComponentSync(tool.agentName, props.artifactKey);
  }, [tool, props.artifactKey]);

  const storeWidget = getWidgetStores().widgetStore.useWidget();

  useLayoutEffect(() => {
    if (!props.artifactKey || props.data == null || !tool) return;
    const widgetStore = getWidgetStores().widgetStore.useWidgetStore;
    // The binding state's index signature widens `setWidget` to `unknown`;
    // narrow at this single seam (mirrors the chat-store pattern of casting
    // at the call site rather than enumerating every action on the binding).
    const state = widgetStore.getState() as unknown as {
      widget: unknown;
      setWidget: (widget: unknown) => void;
    };
    const previous = state.widget;
    const ourSeed = {
      agentName: tool.agentName,
      data: props.data,
      key: props.artifactKey,
      version: 1,
    };
    state.setWidget(ourSeed);
    return () => {
      const current = widgetStore.getState() as unknown as {
        widget: unknown;
        setWidget: (widget: unknown) => void;
      };
      // Race guard: only restore if the store still holds OUR seed. If another
      // mount (rapid prev/next) has already taken over, leaving its seed in
      // place is correct — clobbering with our stale `previous` would corrupt
      // the active widget for one frame.
      if (current.widget === ourSeed) {
        current.setWidget(previous);
      }
    };
  }, [tool, props.artifactKey, props.data]);

  if (!WidgetComponent || props.data == null) {
    return <>{props.fallback}</>;
  }

  // Gate widget render until the store has been seeded with OUR artifact's
  // data (F3): the widget kind key is shared across artifacts of the same
  // kind (e.g. two briefs), so comparing on `key` would let stale data leak
  // for one frame when navigating between same-kind artifacts. Comparing on
  // `data` reference identity guarantees the store actually holds the
  // payload we're about to render — `useQuery`-returned data is stable.
  // `useLayoutEffect` runs after the first commit but before paint, so the
  // initial mount returns the fallback for one frame, then re-renders with
  // the seeded data before the browser paints. Without this gate the
  // widget's internal `widgetStore.useWidget()` would dereference the
  // previously-mounted (or null) widget and throw.
  if (!storeWidget || storeWidget.data !== props.data) {
    return <>{props.fallback}</>;
  }

  // The registry types widgets as `React.ComponentType<any>` because each
  // widget's `data` shape differs. Narrowing at this seam to a `data:
  // unknown` contract matches the chat side panel's mounting pattern
  // (`SidePanel.tsx` does the same cast). `readOnly` is always set here — this
  // mount only exists for the Library preview, which is never editable — so
  // widgets hide their mutating/navigational actions.
  const TypedWidget = WidgetComponent as React.ComponentType<
    { data: unknown; readOnly?: boolean } & Record<string, unknown>
  >;
  return <TypedWidget data={props.data} readOnly {...props.widgetProps} />;
}
